'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../../clients/testerClient';
import { useSignalsQueryInvalidation } from './useSignalsQueryInvalidation';
import type { SignalsSelection } from './useSignalsSelection';

export function useSignalsBulkDelete({
  selection,
  selectionScopeKey,
  removeSignals,
  refetch,
}: {
  selection: SignalsSelection;
  selectionScopeKey: string;
  removeSignals: (signalIds: string[]) => void;
  refetch: () => unknown;
}) {
  const tSignals = useTranslations('signals');
  const { invalidateSignalCounts } = useSignalsQueryInvalidation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingSignalIds, setPendingSignalIds] = useState<string[]>([]);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const preparationIdRef = useRef(0);
  const selectionScopeKeyRef = useRef(selectionScopeKey);
  const previousSelectionScopeKeyRef = useRef<string | null>(null);
  const {
    selectedCount,
    getSelectedSignals,
    clear: clearSelection,
  } = selection;

  selectionScopeKeyRef.current = selectionScopeKey;

  const cancel = useCallback(() => {
    preparationIdRef.current += 1;
    setPendingSignalIds([]);
    setIsDialogOpen(false);
    setIsPreparing(false);
  }, []);

  useEffect(() => {
    if (previousSelectionScopeKeyRef.current === null) {
      previousSelectionScopeKeyRef.current = selectionScopeKey;

      return;
    }

    if (previousSelectionScopeKeyRef.current === selectionScopeKey) {
      return;
    }

    previousSelectionScopeKeyRef.current = selectionScopeKey;
    cancel();
    clearSelection();
  }, [cancel, clearSelection, selectionScopeKey]);

  const prepare = useCallback(async () => {
    if (selectedCount === 0 || isPreparing) return;

    const preparationId = preparationIdRef.current + 1;
    const preparationScopeKey = selectionScopeKeyRef.current;

    preparationIdRef.current = preparationId;
    setIsPreparing(true);
    setPendingSignalIds([]);

    try {
      const signalIds = (await getSelectedSignals()).map((signal) => signal.id);

      if (
        preparationIdRef.current !== preparationId ||
        selectionScopeKeyRef.current !== preparationScopeKey
      ) {
        return;
      }

      if (signalIds.length === 0) {
        clearSelection();
        toasterApi.warning({
          title: tSignals('bulkActions.deleteNoSignalsTitle'),
          description: tSignals('bulkActions.deleteNoSignalsDescription'),
        });

        return;
      }

      setPendingSignalIds(signalIds);
      setIsDialogOpen(true);
    } catch (error) {
      console.error('Failed to prepare signals for deletion:', error);
      toasterApi.error({
        title: tSignals('bulkActions.deleteErrorTitle'),
        description: tSignals('bulkActions.deleteErrorDescription'),
      });
    } finally {
      if (preparationIdRef.current === preparationId) {
        setIsPreparing(false);
      }
    }
  }, [
    clearSelection,
    getSelectedSignals,
    isPreparing,
    selectedCount,
    tSignals,
  ]);

  const confirm = useCallback(async () => {
    if (pendingSignalIds.length === 0) return;

    const signalIds = pendingSignalIds;

    setIsDeleting(true);

    try {
      await terraProxyApiClient.deleteSignalsBulk(signalIds);
      removeSignals(signalIds);
      toasterApi.success({
        title: tSignals('bulkActions.deleteSuccessTitle'),
        description: tSignals('bulkActions.deleteSuccessDescription', {
          count: signalIds.length,
        }),
      });
      clearSelection();
      cancel();
      void refetch();
      invalidateSignalCounts();
    } catch (error) {
      console.error('Failed to delete signals:', error);
      toasterApi.error({
        title: tSignals('bulkActions.deleteErrorTitle'),
        description: tSignals('bulkActions.deleteErrorDescription'),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    cancel,
    clearSelection,
    invalidateSignalCounts,
    pendingSignalIds,
    refetch,
    removeSignals,
    tSignals,
  ]);

  return {
    isDialogOpen,
    pendingCount: pendingSignalIds.length,
    isDeleting,
    isBusy: isDialogOpen || isPreparing || isDeleting,
    prepare,
    confirm,
    cancel,
  };
}
